
import { Investor, Contact } from "@/types/investor";
import { matchContactsToInvestors } from "@/utils/investorAnalysis";
import { clearProcessingProgress } from "@/utils/investors/investorStateUtils";
import { useSaveInvestorData } from "./useSaveInvestorData";

/**
 * Hook for handling file data operations
 */
export const useFileDataHandler = (
  setInvestors: (investors: Investor[]) => void,
  setContacts: (contacts: Contact[]) => void,
  setProcessedIds: (processedIds: Set<string>) => void
) => {
  const { saveDataToSupabaseAndLocal } = useSaveInvestorData();
  
  /**
   * Merge new data with existing data
   */
  const mergeData = (existingInvestors: Investor[], newInvestors: Investor[], existingContacts: Contact[], newContacts: Contact[]) => {
    // Create maps for faster lookups
    const investorMap = new Map<string, Investor>();
    const contactMap = new Map<string, Contact>();
    
    // Add existing investors/contacts to maps
    existingInvestors.forEach(investor => {
      investorMap.set(investor.firm.toLowerCase(), investor);
    });
    
    existingContacts.forEach(contact => {
      // Create a unique key using name and firm
      const key = `${contact.name.toLowerCase()}:${contact.firmName.toLowerCase()}`;
      contactMap.set(key, contact);
    });
    
    // Process new investors - update if exists or add if new
    const mergedInvestors: Investor[] = [...existingInvestors];
    const newInvestorIds: string[] = [];
    
    newInvestors.forEach(newInvestor => {
      const existingInvestor = investorMap.get(newInvestor.firm.toLowerCase());
      
      if (existingInvestor) {
        // Preserve enriched data and merge new data
        const updatedInvestor = {
          ...existingInvestor,
          // Only update these fields if they have values in the new data
          name: newInvestor.name || existingInvestor.name,
          website: newInvestor.website || existingInvestor.website,
          // Merge arrays (keywords and portfolio companies) without duplicates
          keywords: [...new Set([
            ...(existingInvestor.keywords || []), 
            ...(newInvestor.keywords || [])
          ])],
          portfolioCompanies: [...new Set([
            ...(existingInvestor.portfolioCompanies || []), 
            ...(newInvestor.portfolioCompanies || [])
          ])],
          location: {
            city: newInvestor.location?.city || existingInvestor.location?.city || "",
            state: newInvestor.location?.state || existingInvestor.location?.state || ""
          }
        };
        
        // Update in the existing array
        const index = mergedInvestors.findIndex(inv => inv.id === existingInvestor.id);
        if (index !== -1) {
          mergedInvestors[index] = updatedInvestor;
        }
      } else {
        // Add new investor
        mergedInvestors.push(newInvestor);
        newInvestorIds.push(newInvestor.id);
      }
    });
    
    // Process new contacts - update if exists or add if new
    const mergedContacts: Contact[] = [...existingContacts];
    
    newContacts.forEach(newContact => {
      const key = `${newContact.name.toLowerCase()}:${newContact.firmName.toLowerCase()}`;
      const existingContact = contactMap.get(key);
      
      if (existingContact) {
        // Update contact with new data while preserving existing data
        const updatedContact = {
          ...existingContact,
          email: newContact.email || existingContact.email,
          role: newContact.role || existingContact.role,
          linkedin: newContact.linkedin || existingContact.linkedin,
          twitter: newContact.twitter || existingContact.twitter,
          phone: newContact.phone || existingContact.phone,
          location: newContact.location || existingContact.location,
          notes: newContact.notes 
            ? `${existingContact.notes ? existingContact.notes + "\n\n" : ""}${newContact.notes}`
            : existingContact.notes
        };
        
        // Update in the existing array
        const index = mergedContacts.findIndex(c => c.id === existingContact.id);
        if (index !== -1) {
          mergedContacts[index] = updatedContact;
        }
      } else {
        // Add new contact
        mergedContacts.push(newContact);
      }
    });
    
    // Match new contacts with investors
    const result = matchContactsToInvestors(mergedInvestors, mergedContacts);
    
    return {
      investors: result,
      contacts: mergedContacts
    };
  };
  
  /**
   * Handle data from uploaded files
   */
  const handleFileData = (investors: Investor[], contacts: Contact[], shouldMerge = false) => {
    if (shouldMerge) {
      console.log("Merging new data with existing data...");
      
      // Get existing data from database
      // This is now assuming we have existing data in memory or should load from the database
      // instead of localStorage
      const { investors: existingInvestors, contacts: existingContacts } = { 
        investors: [], 
        contacts: [] 
      };
      
      // Merge data
      const { investors: mergedInvestors, contacts: mergedContacts } = mergeData(
        existingInvestors, 
        investors, 
        existingContacts, 
        contacts
      );
      
      console.log(`Merged data: ${mergedInvestors.length} investors, ${mergedContacts.length} contacts`);
      
      // Update state with merged data
      setInvestors(mergedInvestors);
      setContacts(mergedContacts);
      
      // Save merged data to database only
      saveDataToSupabaseAndLocal(mergedInvestors, mergedContacts, new Set());
    } else {
      // Original behavior - replace data
      clearProcessingProgress();
      setProcessedIds(new Set());
      
      // Match contacts to investors based on domain/company name
      const enhancedInvestors = matchContactsToInvestors(investors, contacts);
      
      // Ensure all investors have relevance score set to 0, ignoring any value from Excel
      // And ensure enrichedWith is not present initially
      const cleanedInvestors = enhancedInvestors.map(investor => ({
        ...investor,
        relevanceScore: 0,
        enrichedWith: undefined
      }));
      
      setInvestors(cleanedInvestors);
      setContacts(contacts);
      
      // Save data directly to database without localStorage
      saveDataToSupabaseAndLocal(cleanedInvestors, contacts, new Set());
    }
    
    console.log(`handleFileData: Processed ${contacts.length} contacts`);
  };
  
  /**
   * Handle reprocessing of data
   */
  const handleReprocess = (
    loadProcessingProgress: () => { investors: Investor[], contacts: Contact[] }
  ) => {
    // Get the current data from database through the provided function
    const { investors, contacts } = loadProcessingProgress();
    
    if (!investors || investors.length === 0) {
      return false;
    }
    
    // Clear processing progress
    clearProcessingProgress();
    setProcessedIds(new Set());
    
    // Reset the investors to their initial state (with contacts matched)
    // Ensure relevance scores are reset to 0 and enrichedWith is removed
    const resetInvestors = investors.map(investor => ({
      ...investor,
      relevanceScore: 0,
      lastRelevanceUpdate: null,
      enrichedWith: undefined
    }));
    
    setInvestors(resetInvestors);
    saveDataToSupabaseAndLocal(resetInvestors, contacts, new Set());
    
    return true;
  };
  
  return { handleFileData, handleReprocess, mergeData };
};
